import { SIGN_UP_INPUT_FIELD_ERROR } from "@faire/deprecated-retailer-visitor-shared/consts/sign-up-input-field-error";
import { trackEvent } from "@faire/web/common/trackEvent";
import isArray from "lodash/isArray";
import once from "lodash/once";
import { useState, useMemo, useEffect, useCallback } from "react";

import { IFormField } from "./types";

export const useFormField = <ValueType>(
  initialValue: ValueType,
  eventName: string,
  validator: (value: ValueType) => SIGN_UP_INPUT_FIELD_ERROR | false,
  eventPrefix: string = "sign-up"
): IFormField<ValueType> => {
  const [value, setValue] = useState<ValueType>(initialValue);
  const [error, setErrorState] = useState<string | undefined>(undefined);

  const firstChangeEvent = useMemo(
    () =>
      once((query: string = "") =>
        trackEvent(`/open/${eventPrefix}/change/${eventName}${query}`)
      ),
    [eventPrefix, eventName]
  );

  useEffect(() => {
    const shouldFireEvent = isArray(initialValue)
      ? initialValue.length !== 0
      : initialValue;
    if (shouldFireEvent) {
      firstChangeEvent("?prefill=true");
    }
  }, [initialValue, firstChangeEvent]);

  const isInvalid = useMemo(() => {
    return validator(value);
  }, [value, validator]);

  const selectValue = useCallback(
    (newValue: ValueType) => {
      firstChangeEvent();
      setValue(newValue);
      setErrorState(undefined);
    },
    [firstChangeEvent]
  );

  const setError = useCallback((error: string | undefined | false) => {
    setErrorState(error ? error : undefined);
  }, []);

  return {
    value,
    error,
    isInvalid,
    selectValue,
    setError,
  };
};
